import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Calculator, MoreVertical, ListPlus, Loader2, Columns3, Eye, EyeOff, Download, Filter, X, Search, SlidersHorizontal } from 'lucide-react';
import { userPrefService } from '../../services/userPrefService';
import DataTableFilterPanel from './DataTableFilterPanel';
import DateRangePicker from './DateRangePicker';

// Tarih hunisi DateRangePicker ile seçilir; süzgeç değeri 'YYYY-MM-DD' kalır (sunucu/istemci süzgeci değişmez)
const _isoToDate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const _dateToIso = (d) => (d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : '');
import {
  OPS, colFilterToRule, countRules, emptyRoot, evalGroup, ruleComplete, ruleToColFilter,
} from './tableFilterEngine';

// Kolon tercihleri (göster/gizle · sıra · genişlik) KULLANICI-bazlı sunucuda da tutulur
// (rpt_user_pref: `table.<tableId>`). localStorage cihaz-bağımlıdır; sunucu kopyası sayesinde
// başka cihaz/tarayıcı/oturumda da AYNI düzen gelir ("her gir çıktıda değişmesin").
// localStorage = anında çizim (soğuk açılış), sunucu = kaynak otorite (mount'ta gelince uygulanır).
// getAll uygulama başına BİR kez çekilir (tüm tablolar paylaşır); hata sessiz (yerelle devam).
let _serverPrefsPromise = null;
function loadServerTablePrefs() {
  if (!_serverPrefsPromise) {
    _serverPrefsPromise = userPrefService.getAll()
      .then((r) => (r && r.data) || r || {})
      .catch(() => ({}));
  }
  return _serverPrefsPromise;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const STORAGE_PREFIX = 'vertex.table.colWidths.';
const STORAGE_HIDDEN_PREFIX = 'vertex.table.hiddenCols.';
const STORAGE_ORDER_PREFIX = 'vertex.table.colOrder.';
const STORAGE_FILTERS_PREFIX = 'vertex.table.colFilters.';
const STORAGE_SORT_PREFIX = 'vertex.table.sort.';
const STORAGE_CALC_PREFIX = 'vertex.table.calc.';
const STORAGE_ADV_PREFIX = 'vertex.table.advFilter.';
const STORAGE_SAVEDF_PREFIX = 'vertex.table.savedFilters.';

// Basit JSON localStorage okuma/yazma (sıralama + hesap satırı tercihleri)
function loadPref(prefix, tableId) {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(prefix + tableId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function savePref(prefix, tableId, value) {
  if (!tableId) return;
  try {
    const empty = value == null || (Array.isArray(value) ? !value.length : typeof value === 'object' && !Object.keys(value).length);
    if (empty) localStorage.removeItem(prefix + tableId);
    else localStorage.setItem(prefix + tableId, JSON.stringify(value));
  } catch { /* yok say */ }
}

// Kolon alt satırı hesapları (ClickUp "Calculate") — sayısal olanlar yalnız sayı içeren kolonda
export const CALC_FNS = [
  { key: 'sum', label: 'Toplam', numeric: true },
  { key: 'avg', label: 'Ortalama', numeric: true },
  { key: 'min', label: 'En küçük', numeric: true },
  { key: 'max', label: 'En büyük', numeric: true },
  { key: 'count', label: 'Satır sayısı' },
  { key: 'filled', label: 'Dolu' },
  { key: 'empty', label: 'Boş' },
  { key: 'unique', label: 'Tekil değer' },
];
const _fmtCalc = (v) => (v == null || Number.isNaN(v) ? '—'
  : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 }));
const _collator = typeof Intl !== 'undefined' ? new Intl.Collator('tr', { numeric: true, sensitivity: 'base' }) : null;
const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 800;

// Sütun huni filtreleri — kolon düzeni gibi KALICI: sayfa değişince/geri gelince seçim durur.
// Yalnız DataTable'ın KENDİ yönettiği (kontrolsüz) modda saklanır; kontrollü modda sayfa yönetir.
function loadColFilters(tableId) {
  if (!tableId) return {};
  try {
    const raw = localStorage.getItem(STORAGE_FILTERS_PREFIX + tableId);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}
function saveColFilters(tableId, filters) {
  if (!tableId) return;
  try {
    const has = filters && Object.keys(filters).length;
    if (has) localStorage.setItem(STORAGE_FILTERS_PREFIX + tableId, JSON.stringify(filters));
    else localStorage.removeItem(STORAGE_FILTERS_PREFIX + tableId);
  } catch {
    /* kota dolu / private mode — filtre yine çalışır, sadece kalıcı olmaz */
  }
}

function loadColOrder(tableId) {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(STORAGE_ORDER_PREFIX + tableId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveColOrder(tableId, order) {
  if (!tableId) return;
  try {
    localStorage.setItem(STORAGE_ORDER_PREFIX + tableId, JSON.stringify(order));
  } catch {}
}

function loadHiddenCols(tableId) {
  if (!tableId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_HIDDEN_PREFIX + tableId);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
/** Başlangıç gizli-kolon kümesi: kullanıcının KAYITLI tercihi varsa o (boş dizi de bir
 *  tercihtir — "hepsini göster"), yoksa kolonların kendi `defaultHidden` bayrağı. */
function initialHiddenCols(tableId, columns) {
  let hasStored = false;
  try {
    hasStored = !!(tableId && localStorage.getItem(STORAGE_HIDDEN_PREFIX + tableId));
  } catch {}
  if (hasStored) return new Set(loadHiddenCols(tableId));
  return new Set((columns || []).filter((c) => c.defaultHidden && c.accessor).map((c) => c.accessor));
}

function saveHiddenCols(tableId, hidden) {
  if (!tableId) return;
  try {
    localStorage.setItem(STORAGE_HIDDEN_PREFIX + tableId, JSON.stringify(hidden));
  } catch {}
}

function loadColWidths(tableId) {
  if (!tableId) return {};
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveColWidths(tableId, widths) {
  if (!tableId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(widths));
  } catch {}
}

function SkeletonRow({ columnCount }) {
  return (
    <tr className="border-b border-gray-100 h-14">
      {Array.from({ length: columnCount }).map((_, i) => (
        <td key={i} className="px-3 sm:px-5 py-3 sm:py-3.5">
          <div className="h-4 bg-gray-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

// Detaya gidip GERİ dönünce en son tıklanan satırı "hover gibi" vurgulamak için (tableId bazında,
// sessionStorage). Scroll restoration ile birlikte: kullanıcı nereden geldiğini görür.
const LASTROW_KEY = 'dt_lastRow';
function loadLastRow(tableId) {
  if (!tableId) return null;
  try { const v = JSON.parse(sessionStorage.getItem(LASTROW_KEY) || '{}')[tableId]; return v === undefined ? null : v; }
  catch { return null; }
}
function saveLastRow(tableId, k) {
  if (!tableId) return;
  try {
    const m = JSON.parse(sessionStorage.getItem(LASTROW_KEY) || '{}');
    if (k === null || k === undefined) delete m[tableId]; else m[tableId] = k;
    sessionStorage.setItem(LASTROW_KEY, JSON.stringify(m));
  } catch { /* kota */ }
}

export default function DataTable({
  columns,
  data,
  loading = false,
  emptyMessage = 'Veri bulunamadı',
  striped = true,
  onRowClick,
  sortable = true,
  pagination = true,
  itemsPerPage = 100,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultSort = null,
  className = '',
  tableId = null,         // localStorage key — kullanıcı kolon genişliklerini saklamak için
  resizable = true,        // kolon genişliği elle ayarlanabilir
  onExportExcel = null,    // () => void — Export menüsünde Excel
  onExportCSV = null,      // () => void — Export menüsünde CSV
  exportLanguages = null,  // [{code,label}] — verilirse Export menüsü dil sorar,
                           // onExportExcel/CSV o dil koduyla çağrılır. Yoksa argümansız.
  exportLabel = null,      // Export düğmesi metni (ör. seçim varsa "İndir (3)"); yoksa "Export"
  exportCaption = null,    // menü başında küçük açıklama — NE indirileceğini söyler
  exportExtra = null,      // ek menü satırları: [{ label, onClick }] (ör. "Seçimi yok say — Excel")
  expandedRender = null,   // (row) => ReactNode — expand satırı içeriği (verilirse ok kolonu eklenir)
  initialExpandedKeys = null, // dizi — ilk açılışta açık gelecek satır anahtarları (bağlantıdan odak)
  getRowKey = null,        // (row) => string|number — expand state için key, default row.id veya JSON
  treeParent = null,       // (row) => ebeveyn satırın getRowKey'i | null — AĞAÇ tablosu (düzleştirilmiş). Verilirse
                           // sıralama hiyerarşiyi bozmaz: kardeşler kendi içinde sıralanır, çocuk ebeveyninin altında kalır.
  headerLeft = null,       // ReactNode — toolbar'ın sol tarafı (arama vs.)
  toolbarExtra = null,     // ReactNode — Export butonunun solunda ek toolbar aksiyonu (İçe Aktar vs.)
  maxHeight = null,        // CSS değer (ör. '70vh') — verilirse iç dikey scroll + sabit (sticky) başlık
  rowClassName = null,     // (row, index) => string — koşullu satır vurgusu (varsa striped'ı ezer)
  selectable = false,      // OPT-IN toplu seçim: satır başına kutu + başlıkta tümünü seç.
                           // Varsayılan KAPALI — mevcut tabloların hiçbiri etkilenmez.
  selectedKeys = null,     // dizi/Set — seçili satır anahtarları (getRowKey ile aynı anahtar)
  onSelectionChange = null,// (nextKeysArray) => void
  isRowSelectable = null,  // (row) => bool — seçilemeyen satırda kutu pasif
  rowSelectDisabledTitle = null, // string | (row)=>string — pasif kutunun SEBEBİ (tooltip).
                           // Sebepsiz pasif kutu "bozuk" gibi okunuyor (masraf onayında yaşandı).
  wrapHeaders = false,     // başlıklar çok satıra sarsın (dar kolonlar için yer kazanır)
  dense = false,           // sıkı satır aralığı — çok satırlı listelerde göz kaymasın
  compact = false,         // sıkı YATAY boşluk (px-2/px-3) — çok kolonlu tablo sayfaya sığsın
  fixedLayout = false,     // table-layout: fixed — `width` verilen kolonlar sabit kalır, KALAN alan
                           // genişliksiz kolon(lar)a gider; hücre içeriği taşmaz, kesilir (…).
                           // Varsayılan auto'da `whitespace-nowrap` hücreler tabloyu sayfadan taşırıyordu.
  fitWidth = false,        // İlk açılışta tablo kabın genişliğine SIĞAR: kolonların `width`'i ağırlık sayılır,
                           // ölçülen genişliğe orantılı ölçeklenir (yatay kaydırma yok). Kullanıcı bir kolonu
                           // sürükleyip genişletince o anki tüm genişlikler kaydedilir ve ondan sonra kullanıcının
                           // ayarı geçerlidir; "Genişlikleri sıfırla" yeniden sığdırır (portal kararı 16.10.2026).
  columnFilters = undefined,  // Excel-tablo deseni: kolon başlığında huni filtresi + aktif filtre chip'leri.
                           // VARSAYILAN: DataTable kendi sayfalamasını yönetiyorsa (client-side) AÇIK;
                           // sayfa kendi sayfalamasını yönetiyorsa (pagination={false}) KAPALI (çakışma
                           // / yanıltıcı 'yalnız yüklü sayfa' filtresi olmasın). Prop ile açıkça ez.
                           // Kolon config: filterable(false→huni yok), filterType('values'|'range'|'text'),
                           // filterFormat(v,row)→facet string, filterValues(row)→string[] (satır
                           // birden çok değere üyeyse; ör. karışık divizyon), filterNumber(row)→sayı.
  // Sayfanın KENDİ filtreleri (arama/durum/tarih gibi tablo dışındakiler) aynı "Aktif" çubuğunda
  // gösterilsin diye. Kullanıcı için tek bir filtre yeri olur; yoksa üst çubuk ve kolon hunileri
  // iki ayrı sistem gibi davranır ve "Tümünü temizle" yarısını temizler (24.08 bulgusu).
  externalFilters = null,       // [{key, label, onClear}] — tablo dışı aktif filtreler
  onClearAllFilters = null,     // verilirse "Tümünü temizle" bunu çağırır (kolon + dış filtreler)
  columnFilterValue = null,     // KONTROLLÜ mod: {accessor: filterObj}. Verilirse iç state yerine bu.
  onColumnFilterChange = null,  // (accessor, nextFilterOrNull) => void — kontrollü modda mutasyon.
  getFacetOptions = null,       // (accessor) => [{value,count}] — kontrollü modda değer listesi (facet).
                                // Kontrollü modda: sayfa filtreleme+sayfalama+facet'i yönetir; DataTable
                                // yalnız huni ARAYÜZÜNÜ çizer (server-sayfalı Teklifler/Siparişler için).
  // SUNUCU-TARAFLI SIRALAMA (opt-in). Verilirse tıklama sayfaya bildirilir ve DataTable
  // veriyi KENDİ sıralamaz — server-sayfalı listede yalnız yüklü sayfayı sıralamak
  // (18.000 üründe ilk 100'ü) kullanıcıya "tüm katalog sıralandı" yanılgısı verirdi.
  sortValue = null,             // {key, direction} — kontrollü sıralama durumu
  onSortChange = null,          // ({key, direction}) => void
  filterableColumns = null,     // [accessor,...] beyaz liste — verilirse YALNIZ bu kolonlarda huni çıkar
                                // (server-sayfalı sayfada yalnız server-facet kolonlarına funnel koymak için).
  isGroupRow = null,            // (row) => bool — AĞAÇ tablosunda (treeParent) GRUP başlığı satırı: huni/gelişmiş
                                // süzgeç grup satırına uygulanmaz, grup yalnız en az bir çocuğu süzgeçte kalırsa
                                // görünür; değer listeleri ve alt satır hesapları yalnız yaprak satırlardan.
  onFilteredDataChange = null,  // (rows) => void — huni + gelişmiş süzgeçten geçen satırlar (KPI'ları tabloyla
                                // aynı süzgeçten beslemek için). Yalnız süzülmüş küme değişince çağrılır.
}) {
  // Otomatik varsayılan: client-side (DataTable-sayfalı) tablolar filtre alır; server/harici-sayfalı almaz.
  const columnFiltersOn = columnFilters === undefined ? (pagination !== false) : !!columnFilters;
  const _fltControlled = columnFilterValue != null || typeof onColumnFilterChange === 'function';
  const _isGroup = useCallback((r) => { try { return !!(isGroupRow && isGroupRow(r)); } catch { return false; } }, [isGroupRow]);
  const leafData = useMemo(() => (isGroupRow ? (data || []).filter((r) => !_isGroup(r)) : (data || [])), [data, isGroupRow, _isGroup]);
  // Sıralama ÇOK SEVİYELİ (ClickUp/Excel deseni): [{key, direction}] — ilk öğe birincil. Başlık tıklaması
  // artan → azalan → kapalı döngüsü; Shift+tıklama ya da menüdeki "Sonra bu sütuna göre" ikincil seviye
  // ekler. Kullanıcı değiştirince tableId'ye kaydedilir (yoksa defaultSort). Kontrollü (sunucu) modda tek seviye.
  const _defaultSorts = () => (defaultSort?.key ? [{ key: defaultSort.key, direction: defaultSort.direction || 'asc' }] : []);
  const [sorts, setSorts] = useState(() => {
    const st = loadPref(STORAGE_SORT_PREFIX, tableId);
    return Array.isArray(st) ? st : _defaultSorts();
  });
  const sortConfig = sorts[0] || { key: null, direction: 'asc' };
  const _sortControlled = typeof onSortChange === 'function';
  const effSort = _sortControlled ? (sortValue || { key: null, direction: 'asc' }) : sortConfig;
  const effSorts = _sortControlled ? (sortValue?.key ? [sortValue] : []) : sorts;
  const commitSorts = useCallback((next) => {
    setSorts(next);
    savePref(STORAGE_SORT_PREFIX, tableId, next);
  }, [tableId]);
  // Sıralamayı uygula (tek seviye) — kontrollü modda sayfaya bildir, aksi halde iç state.
  const applySort = useCallback((key, direction) => {
    if (_sortControlled) onSortChange({ key, direction });
    else commitSorts(key ? [{ key, direction }] : []);
  }, [_sortControlled, onSortChange, commitSorts]);
  // İkincil seviye ekle / yönünü değiştir (yalnız istemci sıralamasında)
  const addSort = useCallback((key, direction) => {
    if (_sortControlled) { onSortChange({ key, direction }); return; }
    const rest = sorts.filter((s) => s.key !== key);
    commitSorts([...rest, { key, direction }].slice(-4));
  }, [_sortControlled, onSortChange, sorts, commitSorts]);
  const removeSort = useCallback((key) => {
    if (_sortControlled) { onSortChange({ key: null, direction: 'asc' }); return; }
    commitSorts(sorts.filter((s) => s.key !== key));
  }, [_sortControlled, onSortChange, sorts, commitSorts]);
  // Kolon alt satırı hesapları {accessor: fnKey}
  const [calcs, setCalcs] = useState(() => loadPref(STORAGE_CALC_PREFIX, tableId) || {});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(itemsPerPage);

  // Kolon genişlikleri — localStorage'tan yükle
  const [colWidths, setColWidths] = useState(() => loadColWidths(tableId));
  // Gizli kolonlar — localStorage'tan. Kullanıcının HİÇ tercihi yoksa kolonun kendi
  // `defaultHidden` bayrağı geçerli olur (opt-in: dar ekranda önemli kolonlar sağa taşmasın).
  // "Kayıt yok" ile "kullanıcı hepsini göster dedi" ayrımı için ham değere bakılır —
  // boş dizi SAKLIYSA bu bilinçli bir tercihtir, defaultHidden onu EZMEZ.
  const [hiddenCols, setHiddenCols] = useState(() => initialHiddenCols(tableId, columns));
  // Kolon sırası — localStorage'tan (ilk kolon sabit, sadece 1..N için sıra tutulur)
  const [colOrder, setColOrder] = useState(() => loadColOrder(tableId));
  const [dragAccessor, setDragAccessor] = useState(null);
  const [dropTargetAccessor, setDropTargetAccessor] = useState(null);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  // Sütun seçici PORTAL'da çizilir: tablo kısa olunca (1-2 satır) kökteki overflow-hidden
  // menüyü kesiyordu (partner iskonto paneli, 16.10.2026). Konum düğmenin ekrandaki yerinden.
  const colPickerBtnRef = useRef(null);
  const [colPickerPos, setColPickerPos] = useState(null);
  const openColPicker = () => {
    const r = colPickerBtnRef.current?.getBoundingClientRect();
    if (r) {
      const below = window.innerHeight - r.bottom - 12;
      setColPickerPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right), maxH: Math.max(220, Math.min(480, below)) });
    }
    setColPickerOpen((p) => !p);
  };
  useEffect(() => {
    if (!colPickerOpen) return undefined;
    const close = () => setColPickerOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [colPickerOpen]);
  const [exportOpen, setExportOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set(initialExpandedKeys || []));
  // Excel-tablo kolon filtreleri (opt-in) — accessor -> {values:[]} | {min,max} | {text}
  const [colFilters, setColFilters] = useState(() => loadColFilters(tableId));
  // Efektif filtre state — kontrollü modda sayfa sağlar, aksi halde iç state.
  const effColFilters = _fltControlled ? (columnFilterValue || {}) : colFilters;
  // Gelişmiş filtre paneli (ClickUp "Filtreler"): VE/VEYA + iç içe grup + tüm koşullar. Kontrolsüz
  // modda kendi durumunda tutulur ve hunilerle VE'lenir; kontrollü (sunucu) modda kurallar huni
  // nesnesine çevrilip sayfaya yazılır (ctlDraft yalnız panelin yarım kurallarını taşır).
  const [advFilter, setAdvFilter] = useState(() => loadPref(STORAGE_ADV_PREFIX, tableId) || emptyRoot());
  const [savedFilters, setSavedFilters] = useState(() => loadPref(STORAGE_SAVEDF_PREFIX, tableId) || []);
  const [fltPanelOpen, setFltPanelOpen] = useState(false);
  const fltBtnRef = useRef(null);
  const [ctlDraft, setCtlDraft] = useState(null);
  const [filterMenu, setFilterMenu] = useState(null);           // açık huninin kolon accessor'ı
  const [filterMenuPos, setFilterMenuPos] = useState({ x: 0, y: 0, maxH: 320 });
  const [filterSearch, setFilterSearch] = useState('');
  // Sunucu-taraflı facet (Step 2): açık huninin değer listesi + yükleniyor; value→label haritası (chip).
  const [menuFacet, setMenuFacet] = useState({ loading: false, items: [], server: false });
  const facetLabelsRef = useRef({});

  const _rowKey = useCallback((row, idx) => {
    if (getRowKey) return getRowKey(row, idx);
    return row?.id ?? row?.material ?? row?.docnum ?? row?.ordernum ?? idx;
  }, [getRowKey]);

  // EN SON tıklanan satır (detaydan geri dönünce "hover gibi" vurgulanır). tableId + onRowClick olan
  // her tabloda otomatik çalışır; scroll restoration ile birlikte kullanıcı nereden geldiğini görür.
  const [lastRowKey, setLastRowKey] = useState(() => loadLastRow(tableId));
  const markRowClicked = useCallback((k) => {
    setLastRowKey(k === undefined ? null : k);
    saveLastRow(tableId, k);
  }, [tableId]);

  const toggleExpand = useCallback((key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // tableId değişirse yeniden yükle
  useEffect(() => {
    setColWidths(loadColWidths(tableId));
    // DİKKAT: bu effect mount'ta DA çalışır → aynı kuralı kullanmazsa useState'teki
    // defaultHidden başlangıcını boş kümeyle ezer (24.08'de tam olarak bu oldu).
    setHiddenCols(initialHiddenCols(tableId, columns));
    setColOrder(loadColOrder(tableId));
    setColFilters(loadColFilters(tableId));
    const st = loadPref(STORAGE_SORT_PREFIX, tableId);
    setSorts(Array.isArray(st) ? st : _defaultSorts());
    setCalcs(loadPref(STORAGE_CALC_PREFIX, tableId) || {});
    setAdvFilter(loadPref(STORAGE_ADV_PREFIX, tableId) || emptyRoot());
    setSavedFilters(loadPref(STORAGE_SAVEDF_PREFIX, tableId) || []);
    setCtlDraft(null);
  }, [tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kolon tercihi SUNUCUDAN uygulanır (kaynak otorite) — localStorage'ın üstüne yazar ve
  // yerele aynalanır (sonraki soğuk açılış anında + doğru). Yalnız gerçek kayıt varsa dokunur;
  // yoksa localStorage/defaultHidden aynen kalır. _touched=false kalır → aşağıdaki yazıcı
  // bunu "kullanıcı değişikliği" sanıp geri yollamaz (echo yok).
  const _touched = useRef(false);
  useEffect(() => {
    if (!tableId) return;
    let alive = true;
    loadServerTablePrefs().then((prefs) => {
      if (!alive) return;
      const sp = prefs && prefs[`table.${tableId}`];
      if (!sp || typeof sp !== 'object') return;
      if (Array.isArray(sp.hidden)) { setHiddenCols(new Set(sp.hidden)); saveHiddenCols(tableId, sp.hidden); }
      if (Array.isArray(sp.order)) { setColOrder(sp.order); saveColOrder(tableId, sp.order); }
      if (sp.widths && typeof sp.widths === 'object') { setColWidths(sp.widths); saveColWidths(tableId, sp.widths); }
      if (sp.calcs && typeof sp.calcs === 'object') { setCalcs(sp.calcs); savePref(STORAGE_CALC_PREFIX, tableId, sp.calcs); }
      if (Array.isArray(sp.savedFilters)) { setSavedFilters(sp.savedFilters); savePref(STORAGE_SAVEDF_PREFIX, tableId, sp.savedFilters); }
    });
    return () => { alive = false; };
  }, [tableId]);

  // Kullanıcı kolon düzenini değiştirince (göster/gizle/sıra/genişlik) sunucuya da yaz —
  // debounce'lu, birleşik payload. _touched YALNIZ gerçek kullanıcı eyleminde açılır (aşağıdaki
  // handler'lar); mount/sunucu-uygulama tetiklemez. Hata sessiz (yerelde zaten kayıtlı).
  const _srvTimer = useRef(null);
  useEffect(() => {
    if (!tableId || !_touched.current) return;
    if (_srvTimer.current) clearTimeout(_srvTimer.current);
    const payload = {
      hidden: Array.from(hiddenCols || []),
      order: colOrder || null,
      widths: colWidths || {},
      calcs: calcs || {},
      savedFilters: savedFilters || [],
    };
    _srvTimer.current = setTimeout(() => {
      userPrefService.set(`table.${tableId}`, payload).catch(() => { /* sessiz — yerel kopya var */ });
    }, 800);
    return () => { if (_srvTimer.current) clearTimeout(_srvTimer.current); };
  }, [tableId, hiddenCols, colOrder, colWidths, calcs, savedFilters]);

  // Huni seçimleri kalıcı (kontrolsüz mod) — sayfa değişip geri gelince filtre durur.
  useEffect(() => {
    if (_fltControlled) return;
    saveColFilters(tableId, colFilters);
  }, [tableId, colFilters, _fltControlled]);

  // Gelişmiş filtre kalıcı (kontrolsüz mod) — huniler gibi sayfaya dönünce durur.
  useEffect(() => {
    if (_fltControlled) return;
    savePref(STORAGE_ADV_PREFIX, tableId, advFilter && advFilter.rules && advFilter.rules.length ? advFilter : null);
  }, [tableId, advFilter, _fltControlled]);

  // Kolon sırası uygula — columns[0] sabit, geri kalanı colOrder'a göre dizilir
  const orderedColumns = useMemo(() => {
    if (!columns?.length) return [];
    const first = columns[0];
    const rest = columns.slice(1);
    if (!colOrder || !colOrder.length) return columns;
    const byAccessor = new Map(rest.map(c => [c.accessor, c]));
    const ordered = [];
    for (const acc of colOrder) {
      const c = byAccessor.get(acc);
      if (c) { ordered.push(c); byAccessor.delete(acc); }
    }
    // Sıra listesinde olmayan yeni kolonlar sona eklensin
    for (const c of rest) {
      if (byAccessor.has(c.accessor)) ordered.push(c);
    }
    return [first, ...ordered];
  }, [columns, colOrder]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hiddenCols.has(c.accessor)),
    [orderedColumns, hiddenCols],
  );

  // Drag-drop kolon yeniden sıralama
  const reorderColumn = useCallback((fromAcc, toAcc) => {
    if (!fromAcc || !toAcc || fromAcc === toAcc) return;
    const first = columns[0]?.accessor;
    if (fromAcc === first || toAcc === first) return; // ilk kolon sabit
    const rest = columns.slice(1).map(c => c.accessor);
    const current = colOrder && colOrder.length
      ? [...colOrder.filter(a => rest.includes(a)), ...rest.filter(a => !colOrder.includes(a))]
      : rest;
    const fromIdx = current.indexOf(fromAcc);
    const toIdx = current.indexOf(toAcc);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...current];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromAcc);
    _touched.current = true;
    setColOrder(next);
    saveColOrder(tableId, next);
  }, [columns, colOrder, tableId]);

  const setCalc = useCallback((accessor, fn) => {
    _touched.current = true;
    setCalcs((prev) => {
      const n = { ...prev };
      if (!fn) delete n[accessor]; else n[accessor] = fn;
      savePref(STORAGE_CALC_PREFIX, tableId, n);
      return n;
    });
  }, [tableId]);

  const toggleColVisible = useCallback((accessor) => {
    _touched.current = true;
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(accessor)) next.delete(accessor);
      else next.add(accessor);
      saveHiddenCols(tableId, Array.from(next));
      return next;
    });
  }, [tableId]);

  const showAllCols = useCallback(() => {
    _touched.current = true;
    setHiddenCols(new Set());
    saveHiddenCols(tableId, []);
  }, [tableId]);

  // ── Excel-tablo kolon filtreleri ──
  const colByAccessor = useMemo(() => new Map((columns || []).map((c) => [c.accessor, c])), [columns]);
  // Efektif filtre tipi — explicit yoksa: sağ-hizalı (sayısal) → range, aksi → values.
  const colFilterType = useCallback((col) => col?.filterType || (col?.align === 'right' ? 'range' : 'values'), []);
  // Bu kolonda huni gösterilsin mi — sentetik/aksiyon kolonları ('_' önekli) ve accessor'sızlar hariç.
  const colFilterable = useCallback(
    (col) => columnFiltersOn && !!col && col.filterable !== false && !!col.accessor
      && (!String(col.accessor).startsWith('_') || !!col.filterType || col.filterable === true)
      && (!filterableColumns || filterableColumns.includes(col.accessor)),
    [columnFiltersOn, filterableColumns]);
  // Bir kolonun FACET/filtre string değeri (çoklu-seçim + metin için)
  const facetValue = useCallback((column, row) => {
    if (column.filterFormat) {
      try { return String(column.filterFormat(row?.[column.accessor], row) ?? '').trim(); } catch { return ''; }
    }
    const v = row?.[column.accessor];
    return v === null || v === undefined ? '' : String(v).trim();
  }, []);
  // values filtresi: filterValues varsa satır birden çok değere üye olabilir (kesişim yeter).
  const facetValues = useCallback((column, row) => {
    if (typeof column.filterValues === 'function') {
      try {
        const raw = column.filterValues(row) || [];
        const vals = raw.map((v) => String(v ?? '').trim());
        return vals.length ? vals : [''];
      } catch { return ['']; }
    }
    return [facetValue(column, row)];
  }, [facetValue]);
  // Range için sayısal değer (ham accessor sayısı; column.filterNumber varsa ondan)
  const facetNumber = useCallback((column, row) => {
    const raw = column.filterNumber ? column.filterNumber(row) : row?.[column.accessor];
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, []);
  const isFilterActive = useCallback((f, col) => {
    if (!f || !col) return false;
    const ft = colFilterType(col);
    if (ft === 'range' || ft === 'daterange') return (f.min !== undefined && f.min !== '' && f.min !== null) || (f.max !== undefined && f.max !== '' && f.max !== null);
    if (ft === 'text') return !!(f.text && f.text.trim());
    return !!(f.values && f.values.length);
  }, [colFilterType]);
  const rowPassesFilter = useCallback((column, f, row) => {
    if (!column || !f) return true;
    const ft = colFilterType(column);
    if (ft === 'daterange') {
      // Tarih aralığı: hücre değeri Date.parse ile epoch'a çevrilir; max GÜN SONU dahil
      const raw = column.filterNumber ? column.filterNumber(row) : Date.parse(row?.[column.accessor] || '');
      const n = Number.isFinite(raw) ? raw : null;
      if (f.min && (n === null || n < Date.parse(f.min))) return false;
      if (f.max && (n === null || n > Date.parse(f.max) + 86399999)) return false;
      return true;
    }
    if (ft === 'range') {
      const n = facetNumber(column, row);
      if (f.min !== undefined && f.min !== '' && f.min !== null && (n === null || n < Number(f.min))) return false;
      if (f.max !== undefined && f.max !== '' && f.max !== null && (n === null || n > Number(f.max))) return false;
      return true;
    }
    if (ft === 'text') {
      const t = (f.text || '').toLocaleLowerCase('tr').trim();
      return !t || facetValue(column, row).toLocaleLowerCase('tr').includes(t);
    }
    if (!f.values || !f.values.length) return true;
    return facetValues(column, row).some((v) => f.values.includes(v));
  }, [facetValue, facetValues, facetNumber, colFilterType]);

  // Aktif filtre girdileri [ [accessor, f, col], ... ]
  const activeFilterEntries = useMemo(() => {
    if (!columnFiltersOn) return [];
    return Object.entries(effColFilters)
      .map(([acc, f]) => [acc, f, colByAccessor.get(acc)])
      .filter(([, f, col]) => isFilterActive(f, col));
  }, [columnFiltersOn, effColFilters, colByAccessor, isFilterActive]);

  // ── Gelişmiş filtre (panel) — huniyle AYNI değer okuyucuları ──
  const advTypeOf = useCallback((acc) => colFilterType(colByAccessor.get(acc)), [colFilterType, colByAccessor]);
  const advGetter = useCallback((acc) => {
    const col = colByAccessor.get(acc);
    if (!col) return null;
    return {
      type: colFilterType(col),
      vals: (row) => facetValues(col, row),
      text: (row) => facetValue(col, row),
      num: (row) => facetNumber(col, row),
      date: (row) => {
        const raw = col.filterNumber ? Number(col.filterNumber(row)) : Date.parse(String(row?.[acc] || '').slice(0, 10));
        return Number.isFinite(raw) ? raw : null;
      },
    };
  }, [colByAccessor, colFilterType, facetValues, facetValue, facetNumber]);
  const advCount = useMemo(
    () => (columnFiltersOn && !_fltControlled ? countRules(advFilter, advTypeOf) : 0),
    [columnFiltersOn, _fltControlled, advFilter, advTypeOf]);

  // Veriyi kolon filtreleri + gelişmiş filtreyle süz. KONTROLLÜ modda sayfa süzer → DataTable dokunmaz.
  const filteredData = useMemo(() => {
    if (!columnFiltersOn || _fltControlled || (!activeFilterEntries.length && !advCount)) return data;
    const passes = (row) => activeFilterEntries.every(([, f, col]) => rowPassesFilter(col, f, row))
      && (!advCount || evalGroup(advFilter, row, advGetter));
    if (isGroupRow && typeof treeParent === 'function') {
      // Grup satırı süzgece girmez; çocuğu kalan grup görünür
      const keep = new Set(); const parents = new Set();
      (data || []).forEach((row) => {
        if (_isGroup(row) || !passes(row)) return;
        keep.add(row);
        try { const pk = treeParent(row); if (pk !== null && pk !== undefined) parents.add(pk); } catch { /* yok */ }
      });
      return (data || []).filter((row, i) => (_isGroup(row)
        ? parents.has(getRowKey ? getRowKey(row, i) : i) : keep.has(row)));
    }
    return (data || []).filter(passes);
  }, [columnFiltersOn, _fltControlled, data, activeFilterEntries, rowPassesFilter, advCount, advFilter, advGetter,
      isGroupRow, _isGroup, treeParent, getRowKey]);

  // Huni menüsündeki değer listesi + sayaç. KONTROLLÜ modda sayfa (getFacetOptions) sağlar (facet API).
  const facetOptions = useCallback((column) => {
    if (getFacetOptions) { try { return getFacetOptions(column.accessor) || []; } catch { return []; } }
    const others = activeFilterEntries.filter(([acc]) => acc !== column.accessor);
    const base = others.length
      ? leafData.filter((row) => others.every(([, f, col]) => rowPassesFilter(col, f, row)))
      : leafData;
    const counts = new Map();
    for (const row of base) {
      for (const v of facetValues(column, row)) {
        counts.set(v, (counts.get(v) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'tr'));
  }, [getFacetOptions, leafData, activeFilterEntries, facetValues, rowPassesFilter]);

  // Kolon menüsü (ClickUp deseni): sıralama · taşıma · hesap · gizle her kolonda; filtre gövdesi yalnız
  // filtrelenebilir kolonda. `column.menu === false` menüyü kapatır.
  const colMenuOn = useCallback(
    (col) => !!col && !!col.accessor && col.menu !== false && (sortable || columnFiltersOn || !!tableId),
    [sortable, columnFiltersOn, tableId]);

  // Menü konumu — EKRANA SIĞDIRILIR (position:fixed olduğu için kırpılmaz ama
  // taşabilir). Eski hesap her zaman AŞAĞI açıyor ve `Math.max(200, …)` ile en az
  // 200px yükseklik dayatıyordu: başlık ekranın altına yakınsa ya da pencere
  // kısaysa (dizüstü) menü alttan taşıyor, değer listesi ve "Tümünü seç"
  // erişilemez oluyordu — "bazı bilgisayarlarda sayfaya sığmıyor" (16.10.2026).
  const MENU_W = 280;
  const MENU_GAP = 6;
  const MENU_MARGIN = 12;
  const openFilterMenu = useCallback((e, accessor) => {
    e.stopPropagation();
    const b = e.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = Math.min(Math.max(MENU_MARGIN, b.left - 12), Math.max(MENU_MARGIN, vw - MENU_W - MENU_MARGIN));
    const below = vh - b.bottom - MENU_GAP - MENU_MARGIN;
    const above = b.top - MENU_GAP - MENU_MARGIN;
    // Aşağısı dar VE yukarısı daha genişse yukarı aç. Yukarı açarken `bottom`
    // ile çapalanır: `top` hesaplansaydı içerik kısa olduğunda menü başlıktan
    // kopup havada dururdu.
    const up = below < 240 && above > below;
    const maxH = Math.max(160, Math.floor(up ? above : below));
    // ⚠️ Çapa da SINIRLANIR: her iki taraf da 160px'ten darsa (çok kısa pencere)
    // taban yükseklik devreye girer ve menü yine ekrandan taşardı. Bu durumda
    // menü başlığın üstüne binebilir — taşıp erişilemez olmasındansa yeğdir.
    const fit = (v) => Math.max(MENU_MARGIN, Math.min(v, vh - MENU_MARGIN - maxH));
    setFilterMenuPos(up
      ? { x, up: true, bottom: fit(vh - b.top + MENU_GAP), maxH }
      : { x, up: false, y: fit(b.bottom + MENU_GAP), maxH });
    setFilterSearch('');
    setFilterMenu((prev) => (prev === accessor ? null : accessor));
  }, []);
  const setColFilter = useCallback((accessor, next) => {
    if (_fltControlled) { if (onColumnFilterChange) onColumnFilterChange(accessor, next); return; }
    setColFilters((prev) => {
      const n = { ...prev };
      if (next === null) delete n[accessor]; else n[accessor] = next;
      return n;
    });
  }, [_fltControlled, onColumnFilterChange]);
  const clearAllColFilters = useCallback(() => {
    if (_fltControlled) {
      if (onColumnFilterChange) Object.keys(columnFilterValue || {}).forEach((acc) => onColumnFilterChange(acc, null));
      setCtlDraft(null);
      return;
    }
    setColFilters({});
    setAdvFilter(emptyRoot());
  }, [_fltControlled, onColumnFilterChange, columnFilterValue]);

  // ── Filtreler paneli ──
  // Panelde seçilebilecek kolonlar = huni çıkan kolonlar (aynı kural, aynı beyaz liste).
  const filterCols = useMemo(
    () => orderedColumns.filter((c) => colFilterable(c)).map((c) => ({
      accessor: c.accessor,
      header: c.filterLabel || (typeof c.header === 'string' ? c.header : c.accessor),
    })),
    [orderedColumns, colFilterable]);
  const _colsRef = useRef(effColFilters);
  _colsRef.current = effColFilters;
  // Kontrollü mod: kurallar → huni nesneleri (sütun başına ilk tam kural), 350 ms debounce
  // (sayıyı yazarken her tuşta sunucuya gidilmesin). Sayfanın setter'ı fonksiyonel → ardışık çağrı güvenli.
  const _ctlTimer = useRef(null);
  const pushCtlRules = useCallback((root) => {
    if (_ctlTimer.current) clearTimeout(_ctlTimer.current);
    _ctlTimer.current = setTimeout(() => {
      const want = {};
      for (const r of root.rules || []) {
        if (r.group || !r.acc || want[r.acc]) continue;
        const f = ruleToColFilter(r, advTypeOf(r.acc));
        if (f) want[r.acc] = f;
      }
      const cur = _colsRef.current || {};
      for (const acc of Object.keys(cur)) if (!want[acc]) setColFilter(acc, null);
      for (const [acc, f] of Object.entries(want)) {
        if (JSON.stringify(cur[acc] || null) !== JSON.stringify(f)) setColFilter(acc, f);
      }
    }, 350);
  }, [advTypeOf, setColFilter]);
  const ctlRootFromCols = useCallback((cols) => ({
    logic: 'and',
    rules: Object.entries(cols || {})
      .map(([acc, f]) => (colByAccessor.get(acc) ? colFilterToRule(acc, f, advTypeOf(acc)) : null))
      .filter(Boolean),
  }), [colByAccessor, advTypeOf]);
  const panelRoot = _fltControlled ? (ctlDraft || ctlRootFromCols(effColFilters)) : advFilter;
  const onPanelChange = useCallback((root) => {
    if (_fltControlled) { setCtlDraft(root); pushCtlRules(root); return; }
    setAdvFilter(root);
  }, [_fltControlled, pushCtlRules]);
  const panelCount = _fltControlled ? countRules(panelRoot, advTypeOf) : advCount;
  const openFilterPanel = useCallback(() => {
    setFilterMenu(null);
    if (_fltControlled) setCtlDraft(null);   // her açılışta sunucu durumundan yeniden kur
    setFltPanelOpen((p) => !p);
  }, [_fltControlled]);
  const panelOptions = useCallback((acc, q) => {
    if (getFacetOptions) return getFacetOptions(acc, q);
    const col = colByAccessor.get(acc);
    if (!col) return [];
    const counts = new Map();
    for (const row of leafData) for (const v of facetValues(col, row)) counts.set(v, (counts.get(v) || 0) + 1);
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'tr'));
  }, [getFacetOptions, colByAccessor, leafData, facetValues]);
  const commitSaved = useCallback((next) => {
    _touched.current = true;
    setSavedFilters(next);
    savePref(STORAGE_SAVEDF_PREFIX, tableId, next);
  }, [tableId]);
  const saveCurrentFilter = useCallback((name) => {
    const cols = {};
    for (const [acc, f] of Object.entries(effColFilters || {})) if (isFilterActive(f, colByAccessor.get(acc))) cols[acc] = f;
    const item = { id: `s${Date.now().toString(36)}`, name, col: cols, adv: _fltControlled ? null : advFilter };
    commitSaved([...savedFilters.filter((x) => x.name !== name), item].slice(-20));
  }, [effColFilters, isFilterActive, colByAccessor, _fltControlled, advFilter, savedFilters, commitSaved]);
  const applySavedFilter = useCallback((item) => {
    const cols = item?.col || {};
    if (_fltControlled) {
      const cur = _colsRef.current || {};
      for (const acc of Object.keys(cur)) if (!cols[acc]) setColFilter(acc, null);
      for (const [acc, f] of Object.entries(cols)) if (colByAccessor.get(acc)) setColFilter(acc, f);
      setCtlDraft(ctlRootFromCols(cols));
      return;
    }
    setColFilters(Object.fromEntries(Object.entries(cols).filter(([acc]) => colByAccessor.get(acc))));
    setAdvFilter(item?.adv && Array.isArray(item.adv.rules) ? item.adv : emptyRoot());
  }, [_fltControlled, setColFilter, colByAccessor, ctlRootFromCols]);
  // Aktif çubuğundaki özet ("Durum: IFC, IFR ve Tarih > …")
  const describeAdv = useCallback((node) => {
    const parts = [];
    for (const r of node.rules || []) {
      if (r.group) { const t = describeAdv(r); if (t) parts.push(`(${t})`); continue; }
      const type = advTypeOf(r.acc);
      if (!ruleComplete(r, type)) continue;
      const col = colByAccessor.get(r.acc);
      const name = col?.filterLabel || (typeof col?.header === 'string' ? col.header : r.acc);
      const opL = ((OPS[type] || OPS.values).find((o) => o.k === r.op) || {}).l || r.op;
      let val = '';
      if (type === 'values') val = (r.values || []).slice(0, 2).map((v) => v || '(boş)').join(', ') + ((r.values || []).length > 2 ? ` +${r.values.length - 2}` : '');
      else if (r.op === 'between') val = `${r.v} – ${r.v2}`;
      else if (r.op !== 'empty' && r.op !== 'not_empty') val = String(r.v);
      parts.push(`${name} ${opL.toLocaleLowerCase('tr')}${val ? ` ${val}` : ''}`);
    }
    return parts.join(node.logic === 'or' ? ' veya ' : ' ve ');
  }, [advTypeOf, colByAccessor]);

  // Dışarı tıkla → huni menüsünü kapat
  useEffect(() => {
    if (!filterMenu) return;
    const h = (e) => { if (!e.target.closest('[data-colmenu-root]') && !e.target.closest('[data-colfunnel]')) setFilterMenu(null); };
    // Konum açılış anında hesaplanıyor → pencere yeniden boyutlanır ya da sayfa
    // kaydırılırsa menü başlıktan kopar. Yeniden konumlandırmak yerine KAPAT:
    // yarı doğru konumda duran menü, kapanandan daha kafa karıştırıcı.
    const close = () => setFilterMenu(null);
    // ⚠️ Menünün KENDİ listesini kaydırmak da 'scroll' olayıdır (capture ile pencereye düşer) —
    // kapanırsa uzun değer listesinin (ör. marka) alt satırlarına hiç inilemez.
    const onScroll = (e) => {
      const t = e.target;
      if (t && t.nodeType === 1 && t.closest && t.closest('[data-colmenu-root]')) return;
      close();
    };
    document.addEventListener('mousedown', h, true);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('mousedown', h, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [filterMenu]);

  // Sunucu-facet: huni açılınca / aramada getFacetOptions(accessor, query) çağır (Promise→lazy yükleme).
  useEffect(() => {
    if (!filterMenu || !getFacetOptions) return;
    const col = colByAccessor.get(filterMenu);
    if (!col || colFilterType(col) !== 'values') return;
    let cancelled = false;
    const record = (arr) => {
      const m = { ...(facetLabelsRef.current[filterMenu] || {}) };
      for (const o of (arr || [])) if (o && o.label != null) m[o.value] = o.label;
      facetLabelsRef.current[filterMenu] = m;
    };
    // 220ms debounce (aramada server'a her tuşta gitme). Tek çağrı; sync/async'i Promise.resolve ayırır.
    setMenuFacet((p) => ({ loading: true, items: p.items, server: p.server }));
    const t = setTimeout(() => {
      const res = getFacetOptions(filterMenu, filterSearch);
      const isAsync = !!(res && typeof res.then === 'function');
      Promise.resolve(res)
        .then((arr) => { if (!cancelled) { record(arr); setMenuFacet({ loading: false, items: arr || [], server: isAsync }); } })
        .catch(() => { if (!cancelled) setMenuFacet({ loading: false, items: [], server: true }); });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filterMenu, filterSearch, getFacetOptions, colByAccessor, colFilterType]);

  // Reset page when data / filters change
  useEffect(() => { setCurrentPage(1); }, [data, pageSize, colFilters, advFilter]);

  // Sticky yatay scrollbar — tablo viewport'tan uzunsa altta sabit görünür
  const scrollContainerRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const [stickyScroll, setStickyScroll] = useState({ visible: false, left: 0, width: 0, contentWidth: 0 });

  // DİKKAT: dependency dizisi [] ZORUNLU. Boş bırakılırsa (deps yok) effect HER render'dan
  // sonra çalışır; içindeki setStickyScroll her seferinde YENİ obje set edip yeni render'ı
  // tetikler → sonsuz döngü ("Maximum update depth exceeded") → DataTable kullanan her sayfa
  // sürekli yeniden render olup CPU'yu yakar (özellikle zayıf makinede kasma). Listener'lar
  // (scroll/resize/ResizeObserver) güncellemeyi zaten yapar; effect mount'ta BİR KEZ kurulmalı.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const hasOverflow = el.scrollWidth > el.clientWidth + 1;
      const tableInView = rect.top < window.innerHeight && rect.bottom > 60;
      const bottomBelowViewport = rect.bottom > window.innerHeight;
      const v = hasOverflow && tableInView && bottomBelowViewport;
      // Değer değişmediyse yeni obje SET ETME (gereksiz render'ı önle — ekstra güvenlik ağı)
      setStickyScroll((prev) =>
        prev.visible === v && prev.left === rect.left && prev.width === rect.width && prev.contentWidth === el.scrollWidth
          ? prev
          : { visible: v, left: rect.left, width: rect.width, contentWidth: el.scrollWidth }
      );
    };
    update();
    // ⚠️ capture:true ZORUNLU. Sayfa window'da DEĞİL, Layout'taki
    // <main className="flex-1 overflow-y-auto"> içinde kayıyor; scroll olayı
    // KABARMAZ (bubble etmez) → window'daki normal dinleyici hiç tetiklenmiyordu.
    // Yakalama fazındaki dinleyici ise herhangi bir torunun scroll'unu görür.
    // Bu olmadan çubuk mount'taki (çoğu zaman veri gelmeden önceki) duruma
    // takılı kalıyor ve kullanıcı yana kaydırmak için sayfanın EN ALTINA
    // inmek zorunda kalıyordu.
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  // ── Sığdırma (fitWidth) — kabın iç genişliği ölçülür, kolonlar orantılı dağıtılır ──
  const [fitBox, setFitBox] = useState(0);
  useEffect(() => {
    if (!fitWidth) return undefined;
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const measure = () => setFitBox((prev) => (Math.abs(prev - el.clientWidth) < 2 ? prev : el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitWidth]);

  const onMainScroll = useCallback(() => {
    const a = scrollContainerRef.current, b = fakeScrollRef.current;
    if (a && b && b.scrollLeft !== a.scrollLeft) b.scrollLeft = a.scrollLeft;
  }, []);
  const onFakeScroll = useCallback(() => {
    const a = scrollContainerRef.current, b = fakeScrollRef.current;
    if (a && b && a.scrollLeft !== b.scrollLeft) a.scrollLeft = b.scrollLeft;
  }, []);

  // Resize state
  const resizingRef = useRef(null); // { accessor, startX, startWidth }

  const onResizeStart = useCallback((e, accessor) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startWidth = th ? th.offsetWidth : 100;
    // Sığdırılmış tabloda ilk sürüklemede TÜM kolonların ekrandaki genişliği alınır: yalnız
    // sürüklenen kolon kaydedilseydi diğerleri ham `width`'lerine döner, tablo zıplardı.
    if (th && th.parentElement) {
      const snap = {};
      th.parentElement.querySelectorAll('th[data-col]').forEach((el) => {
        snap[el.getAttribute('data-col')] = el.offsetWidth;
      });
      setColWidths((prev) => (Object.keys(prev).length ? prev : snap));
    }
    resizingRef.current = { accessor, startX: e.clientX, startWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      const { accessor, startX, startWidth } = resizingRef.current;
      const delta = e.clientX - startX;
      const newWidth = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, startWidth + delta));
      setColWidths((prev) => ({ ...prev, [accessor]: newWidth }));
    };
    const onUp = () => {
      if (resizingRef.current) {
        const accessor = resizingRef.current.accessor;
        resizingRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        _touched.current = true;
        // Save to localStorage with current widths (use functional read)
        setColWidths((prev) => {
          saveColWidths(tableId, prev);
          return prev;
        });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [tableId]);

  const onResetWidths = useCallback(() => {
    _touched.current = true;
    setColWidths({});
    saveColWidths(tableId, {});
  }, [tableId]);

  // Sorting (kolon filtreleri uygulanmış veri üzerinden)
  const sortedData = useMemo(() => {
    // Kontrollü modda veri SUNUCUDAN sıralı gelir; burada yeniden sıralamak
    // yalnız görünen sayfayı karıştırırdı.
    if (_sortControlled) return filteredData;
    const levels = sorts.filter((s) => s?.key);
    if (!levels.length) return filteredData;
    // Sıralama değeri: kolonun sortValue(row) > nesne değerde filterNumber/filterFormat > ham değer.
    // Boş değer YÖNDEN BAĞIMSIZ hep sonda; metin Türkçe + doğal sayı sırası (A2 < A10).
    const getters = levels.map((s) => {
      const col = colByAccessor.get(s.key);
      return (row) => {
        if (col?.sortValue) { try { return col.sortValue(row); } catch { return null; } }
        const raw = row?.[s.key];
        if (raw !== null && typeof raw === 'object' && !(raw instanceof Date)) {
          if (col?.filterNumber) return col.filterNumber(row);
          if (col?.filterFormat) { try { return col.filterFormat(raw, row); } catch { return null; } }
          return null;
        }
        return raw;
      };
    });
    const isEmpty = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
    const cmp = (x, y) => {
      if (typeof x === 'number' && typeof y === 'number') return x - y;
      if (typeof x === 'boolean' || typeof y === 'boolean') return Number(!!x) - Number(!!y);
      if (x instanceof Date || y instanceof Date) return new Date(x) - new Date(y);
      const sx = String(x); const sy = String(y);
      return _collator ? _collator.compare(sx, sy) : sx.localeCompare(sy, 'tr');
    };
    const cmpItems = (a, b) => {
        for (let k = 0; k < levels.length; k += 1) {
          const x = a.keys[k]; const y = b.keys[k];
          const ex = isEmpty(x); const ey = isEmpty(y);
          if (ex && ey) continue;
          if (ex) return 1;
          if (ey) return -1;
          const c = cmp(x, y);
          if (c) return levels[k].direction === 'desc' ? -c : c;
        }
        return a.i - b.i;   // kararlı
    };
    const items = filteredData.map((row, i) => ({ row, i, keys: getters.map((g) => g(row)) }));
    if (typeof treeParent === 'function') {
      // Ağaç: kardeş grupları ayrı sıralanır, sonra derinlik-önce yeniden dizilir (ebeveyni süzgeçte
      // kalmamış satır kök sayılır — kaybolmaz).
      const keyOf = (it) => (getRowKey ? getRowKey(it.row, it.i) : it.i);
      const present = new Set(items.map(keyOf));
      const kids = new Map();
      const roots = [];
      for (const it of items) {
        let pk = null;
        try { pk = treeParent(it.row); } catch { pk = null; }
        if (pk !== null && pk !== undefined && present.has(pk)) {
          if (!kids.has(pk)) kids.set(pk, []);
          kids.get(pk).push(it);
        } else roots.push(it);
      }
      const out = [];
      const emit = (list) => {
        list.sort(cmpItems);
        for (const it of list) {
          out.push(it.row);
          const ch = kids.get(keyOf(it));
          if (ch) emit(ch);
        }
      };
      emit(roots);
      return out;
    }
    return items
      .sort((a, b) => {
        for (let k = 0; k < levels.length; k += 1) {
          const x = a.keys[k]; const y = b.keys[k];
          const ex = isEmpty(x); const ey = isEmpty(y);
          if (ex && ey) continue;
          if (ex) return 1;
          if (ey) return -1;
          const c = cmp(x, y);
          if (c) return levels[k].direction === 'desc' ? -c : c;
        }
        return a.i - b.i;   // kararlı
      })
      .map((x) => x.row);
  }, [filteredData, sorts, _sortControlled, colByAccessor, treeParent, getRowKey]);

  // Süzülmüş küme dışarı (KPI/özet aynı süzgeçten beslensin)
  const _onFiltered = useRef(onFilteredDataChange);
  _onFiltered.current = onFilteredDataChange;
  useEffect(() => {
    if (!_onFiltered.current) return;
    const rows = filteredData || [];
    _onFiltered.current(isGroupRow ? rows.filter((r) => !_isGroup(r)) : rows);
  }, [filteredData, isGroupRow, _isGroup]);

  // Pagination
  const totalPages = pagination
    ? Math.max(1, Math.ceil(sortedData.length / pageSize))
    : 1;

  const safePage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, safePage, pageSize, pagination]);

  // Başlık tıklaması: artan → azalan → kapalı. Shift ile ikincil seviye (istemci sıralamasında).
  const handleSort = (columnKey, e) => {
    if (!sortable) return;
    if (_sortControlled) {
      const dir = (effSort.key === columnKey && effSort.direction === 'asc') ? 'desc' : 'asc';
      applySort(columnKey, dir);
      return;
    }
    const cur = sorts.find((s) => s.key === columnKey);
    if (e?.shiftKey && sorts.length && !(sorts.length === 1 && cur)) {
      if (!cur) addSort(columnKey, 'asc');
      else if (cur.direction === 'asc') addSort(columnKey, 'desc');
      else removeSort(columnKey);
      return;
    }
    if (cur && sorts.length === 1) {
      if (cur.direction === 'asc') applySort(columnKey, 'desc');
      else applySort(null, 'asc');
    } else {
      applySort(columnKey, 'asc');
    }
  };

  // Alt satır hesapları — filtrelenmiş TÜM satırlar üzerinden (yalnız görünen sayfa değil)
  const calcValues = useMemo(() => {
    const out = {};
    for (const [acc, fn] of Object.entries(calcs || {})) {
      const col = colByAccessor.get(acc);
      if (!col) continue;
      const rows = isGroupRow ? sortedData.filter((r) => !_isGroup(r)) : sortedData;
      if (fn === 'count') { out[acc] = rows.length; continue; }
      if (fn === 'filled' || fn === 'empty' || fn === 'unique') {
        const vals = rows.map((r) => facetValue(col, r));
        if (fn === 'filled') out[acc] = vals.filter((v) => v !== '').length;
        else if (fn === 'empty') out[acc] = vals.filter((v) => v === '').length;
        else out[acc] = new Set(vals.filter((v) => v !== '')).size;
        continue;
      }
      const nums = rows.map((r) => facetNumber(col, r)).filter((n) => n !== null);
      if (!nums.length) { out[acc] = null; continue; }
      if (fn === 'sum') out[acc] = nums.reduce((a, b) => a + b, 0);
      else if (fn === 'avg') out[acc] = nums.reduce((a, b) => a + b, 0) / nums.length;
      else if (fn === 'min') out[acc] = Math.min(...nums);
      else if (fn === 'max') out[acc] = Math.max(...nums);
    }
    return out;
  }, [calcs, colByAccessor, sortedData, facetValue, facetNumber, isGroupRow, _isGroup]);
  const hasCalcRow = visibleColumns.some((c) => calcs?.[c.accessor]);
  const colHasNumbers = useCallback((col) => (data || []).some((r) => facetNumber(col, r) !== null), [data, facetNumber]);

  // Smart page numbers (max ~7 visible)
  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 4) pages.push('...');
      const start = Math.max(2, safePage - 2);
      const end = Math.min(totalPages - 1, safePage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 3) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, safePage]);

  const hasCustomWidths = Object.keys(colWidths).length > 0;

  // Sığdırma genişlikleri: kullanıcı ayarı yoksa kolon `width`'leri ağırlık olarak ölçeklenir.
  // ≤56 px kolonlar (ikon düğmesi) sabit kalır; genişliksiz kolon varsayılan ağırlık alır.
  const fitWidths = useMemo(() => {
    if (!fitWidth || hasCustomWidths || !fitBox) return null;
    const extras = (selectable ? 40 : 0) + (expandedRender ? 32 : 0);
    const FLEX_DEFAULT = 200;
    let fixedSum = 0;
    let weightSum = 0;
    for (const c of visibleColumns) {
      const w = typeof c.width === 'number' ? c.width : null;
      if (w != null && w <= 56) fixedSum += w;
      else weightSum += w ?? (c.fitWeight || FLEX_DEFAULT);
    }
    const avail = fitBox - extras - fixedSum;
    if (weightSum <= 0 || avail <= 0) return null;
    const out = {};
    let used = 0;
    let lastFlex = null;
    for (const c of visibleColumns) {
      const w = typeof c.width === 'number' ? c.width : null;
      if (w != null && w <= 56) { out[c.accessor] = w; continue; }
      const px = Math.max(MIN_COL_WIDTH, Math.floor(((w ?? (c.fitWeight || FLEX_DEFAULT)) / weightSum) * avail));
      out[c.accessor] = px;
      used += px;
      lastFlex = c.accessor;
    }
    if (lastFlex && used < avail) out[lastFlex] += avail - used; // yuvarlama artığı
    return out;
  }, [fitWidth, hasCustomWidths, fitBox, visibleColumns, selectable, expandedRender]);

  // ── Toplu seçim (opt-in) ────────────────────────────────────────────────
  // "Tümünü seç" GÖRÜNEN (filtrelenmiş + sayfalanmış) satırları kapsar — kullanıcı
  // göremediği satırı onaylamamalı.
  const selSet = useMemo(
    () => (selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || [])),
    [selectedKeys]
  );
  const selectableKeys = useMemo(() => {
    if (!selectable) return [];
    return paginatedData
      .map((r, i) => ({ r, k: _rowKey(r, i) }))
      .filter(({ r }) => !isRowSelectable || isRowSelectable(r))
      .map(({ k }) => k);
  }, [selectable, paginatedData, isRowSelectable, _rowKey]);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selSet.has(k));
  const someSelected = !allSelected && selectableKeys.some((k) => selSet.has(k));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selSet);
    if (allSelected) selectableKeys.forEach((k) => next.delete(k));
    else selectableKeys.forEach((k) => next.add(k));
    onSelectionChange([...next]);
  };
  const toggleOne = (k) => {
    if (!onSelectionChange) return;
    const next = new Set(selSet);
    if (next.has(k)) next.delete(k); else next.add(k);
    onSelectionChange([...next]);
  };


  return (
    <div className={`rounded-xl overflow-hidden border border-gray-200 ${className}`}>
      {/* Üst toolbar — sütun seçici, export, sol slot (tableId varsa veya headerLeft/export verilmişse) */}
      {(tableId || headerLeft || toolbarExtra || onExportExcel || onExportCSV) && (
        <div className="flex items-center px-3 sm:px-5 py-2 bg-gray-50/80 border-b border-gray-100 gap-2">
          {headerLeft && <div className="flex-1 min-w-0">{headerLeft}</div>}
          {!headerLeft && <div className="flex-1" />}
          {resizable && hasCustomWidths && (
            <button
              onClick={onResetWidths}
              title="Kolon genişliklerini varsayılana sıfırla"
              className="text-xs text-gray-400 hover:text-gray-700 mr-1"
            >
              Genişlikleri sıfırla
            </button>
          )}
          {toolbarExtra}
          {columnFiltersOn && filterCols.length > 0 && (
            <button
              ref={fltBtnRef}
              type="button"
              onClick={openFilterPanel}
              title="Sütunlara göre koşullu filtre (ve / veya, iç içe grup, kayıtlı filtreler)"
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-lg ${
                panelCount > 0 || fltPanelOpen
                  ? 'border-primary-200 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900 hover:bg-white'}`}
            >
              <SlidersHorizontal size={13} />
              Filtreler
              {panelCount > 0 && (
                <span className="px-1 py-0.5 bg-primary-500 text-white rounded text-[10px] font-semibold leading-none">{panelCount}</span>
              )}
            </button>
          )}
          {(onExportExcel || onExportCSV) && (
            <div className="relative">
              <button
                onClick={() => setExportOpen((p) => !p)}
                title="Veriyi Excel veya CSV olarak indir"
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-white bg-white"
              >
                <Download size={13} />
                {exportLabel || 'Export'}
              </button>
              {exportOpen && (
                <>
                  <div className="fixed inset-0 z-[25]" onClick={() => setExportOpen(false)} />
                  <div className={`absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 ${(exportCaption || exportExtra?.length) ? 'min-w-[240px]' : 'min-w-[160px]'}`}>
                    {exportCaption && (
                      <div className="px-3 pt-1.5 pb-1 text-[11px] text-gray-400 border-b border-gray-100 mb-1">{exportCaption}</div>
                    )}
                    {[['Excel', onExportExcel], ['CSV', onExportCSV]]
                      .filter(([, fn]) => !!fn)
                      .map(([fmt, fn]) => (
                        exportLanguages?.length ? (
                          // Dil seçenekli: format bir başlık, altında diller
                          <div key={fmt}>
                            <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              {fmt}
                            </div>
                            {exportLanguages.map((lang) => (
                              <button
                                key={lang.code}
                                onClick={() => { fn(lang.code); setExportOpen(false) }}
                                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                {lang.label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <button
                            key={fmt}
                            onClick={() => { fn(); setExportOpen(false) }}
                            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            {fmt}
                          </button>
                        )
                      ))}
                    {exportExtra?.length > 0 && (
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        {exportExtra.map((x) => (
                          <button
                            key={x.label}
                            onClick={() => { x.onClick?.(); setExportOpen(false) }}
                            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            {x.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="relative">
            <button
              ref={colPickerBtnRef}
              onClick={openColPicker}
              title="Görünür sütunları seç"
              className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-white bg-white"
            >
              <Columns3 size={13} />
              Sütunlar
              {hiddenCols.size > 0 && (
                <span className="px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                  {columns.length - hiddenCols.size}/{columns.length}
                </span>
              )}
            </button>
            {colPickerOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[120]" onClick={() => setColPickerOpen(false)} />
                <div className="fixed z-[121] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[220px] py-1 overflow-y-auto"
                     style={{ top: colPickerPos?.top ?? 80, right: colPickerPos?.right ?? 16, maxHeight: colPickerPos?.maxH ?? 320 }}>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-medium border-b border-gray-100 flex items-center justify-between">
                    <span>Görünür Sütunlar</span>
                    {hiddenCols.size > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); showAllCols() }}
                        className="text-primary-600 hover:underline normal-case text-[10px]"
                      >
                        Tümünü göster
                      </button>
                    )}
                  </div>
                  {columns.map((c) => {
                    const isHidden = hiddenCols.has(c.accessor)
                    return (
                      <label
                        key={c.accessor}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={() => toggleColVisible(c.accessor)}
                          className="rounded"
                        />
                        {isHidden ? <EyeOff size={12} className="text-gray-400" /> : <Eye size={12} className="text-emerald-500" />}
                        <span className={isHidden ? 'text-gray-400' : 'text-gray-800'}>{c.header}</span>
                      </label>
                    )
                  })}
                </div>
              </>,
              document.body,
            )}
          </div>
        </div>
      )}

      {/* Aktif filtre chip'leri — kolon hunileri + sayfanın kendi filtreleri (externalFilters) */}
      {((columnFiltersOn && (activeFilterEntries.length > 0 || advCount > 0)) || (externalFilters || []).length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 sm:px-5 py-2 bg-[#FCFCFE] border-b border-gray-100">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1">Aktif</span>
          {(columnFiltersOn ? activeFilterEntries : []).map(([acc, f, col]) => {
            const ft = colFilterType(col);
            let summary = '';
            if (ft === 'range') summary = `${f.min ?? ''}${(f.min ?? '') !== '' || (f.max ?? '') !== '' ? '–' : ''}${f.max ?? ''}`;
            else if (ft === 'daterange') {
              const dd = (s) => (s ? String(s).split('-').reverse().join('.') : '');
              summary = `${dd(f.min)}${f.min || f.max ? '–' : ''}${dd(f.max)}`;
            }
            else if (ft === 'text') summary = `içerir: ${f.text}`;
            else {
              const vs = f.values || [];
              const lbls = facetLabelsRef.current[acc] || {};
              const disp = (v) => (lbls[v] ?? v) || '(boş)';
              summary = vs.slice(0, 2).map(disp).join(', ') + (vs.length > 2 ? ` +${vs.length - 2}` : '');
            }
            return (
              <span key={acc} className="inline-flex items-center gap-1 rounded-md bg-primary-50 border border-primary-100 text-primary-700 text-xs px-2 py-0.5">
                <span className="font-medium">{col.header}:</span>
                <span className="max-w-[180px] truncate">{summary}</span>
                <button type="button" onClick={() => setColFilter(acc, null)} className="text-primary-400 hover:text-primary-700" title="Filtreyi kaldır">
                  <X size={12} />
                </button>
              </span>
            );
          })}
          {advCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 border border-violet-100 text-violet-700 text-xs px-2 py-0.5">
              <SlidersHorizontal size={11} />
              <button type="button" onClick={openFilterPanel} className="max-w-[360px] truncate hover:underline" title={describeAdv(advFilter)}>
                {describeAdv(advFilter)}
              </button>
              <button type="button" onClick={() => setAdvFilter(emptyRoot())} className="text-violet-400 hover:text-violet-700" title="Koşulları kaldır">
                <X size={12} />
              </button>
            </span>
          )}
          {(externalFilters || []).map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1 rounded-md bg-primary-50 border border-primary-100 text-primary-700 text-xs px-2 py-0.5">
              <span className="max-w-[240px] truncate">{f.label}</span>
              <button type="button" onClick={f.onClear} className="text-primary-400 hover:text-primary-700" title="Filtreyi kaldır">
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => {
              if (onClearAllFilters) { onClearAllFilters(); setAdvFilter(emptyRoot()); setCtlDraft(null); }
              else clearAllColFilters();
            }}
            className="text-xs text-gray-500 hover:text-gray-800 ml-1"
          >
            Tümünü temizle
          </button>
        </div>
      )}

      {/* Table */}
      <div
        ref={scrollContainerRef}
        onScroll={onMainScroll}
        className={maxHeight ? 'overflow-auto' : 'overflow-x-auto'}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className="w-full text-sm" style={{ tableLayout: (fixedLayout || hasCustomWidths) ? 'fixed' : 'auto' }}>
          {/* Header */}
          <thead className={`bg-gray-50 border-b border-gray-200 ${maxHeight ? 'sticky top-0 z-20' : ''}`}>
            <tr>
              {selectable && (
                <th className="w-10 px-3">
                  {/* Seçilebilir satır yoksa kutu PASİF: tıklanıp hiçbir şey yapmaması
                      "tümünü seç bozuk" diye okunuyordu. */}
                  <input type="checkbox" aria-label="Tümünü seç"
                         className="rounded border-gray-300 cursor-pointer disabled:opacity-40
                                    disabled:cursor-not-allowed"
                         disabled={selectableKeys.length === 0}
                         title={selectableKeys.length === 0
                           ? 'Bu listede toplu işlem yapılabilecek satır yok'
                           : 'Tümünü seç'}
                         checked={allSelected}
                         ref={(el) => { if (el) el.indeterminate = someSelected; }}
                         onChange={toggleAll} />
                </th>
              )}
              {expandedRender && <th className="w-8 px-2" />}
              {visibleColumns.map((column, colIdx) => {
                const isSortable = column.sortable !== false && sortable;
                const isActive = effSort.key === column.accessor;
                const customWidth = colWidths[column.accessor] || fitWidths?.[column.accessor];
                const effectiveWidth = customWidth || (typeof column.width === 'number' ? column.width : null);
                // maxWidth vermiyoruz: table-fixed'de kolonların artan alanı doldurmak için BÜYÜMESİNE
                // izin ver → genişlik toplamı sayfadan azsa sağda boşluk kalmaz. Taşmada minWidth +
                // overflow-x-auto scroll'u korur.
                const thStyle = effectiveWidth ? { width: effectiveWidth, minWidth: effectiveWidth } : undefined;
                const isFirstCol = colIdx === 0;
                const isDraggable = !!tableId && !isFirstCol;
                const isDragging = dragAccessor === column.accessor;
                const isDropTarget = dropTargetAccessor === column.accessor && dragAccessor && dragAccessor !== column.accessor;
                return (
                  <th
                    key={column.accessor}
                    data-col={column.accessor}
                    style={thStyle}
                    draggable={isDraggable}
                    onDragStart={(e) => {
                      if (!isDraggable) return;
                      setDragAccessor(column.accessor);
                      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', column.accessor); } catch {}
                    }}
                    onDragEnter={(e) => {
                      if (!isDraggable || !dragAccessor || dragAccessor === column.accessor) return;
                      e.preventDefault();
                      setDropTargetAccessor(column.accessor);
                    }}
                    onDragOver={(e) => {
                      if (!isDraggable || !dragAccessor || dragAccessor === column.accessor) return;
                      e.preventDefault();
                      try { e.dataTransfer.dropEffect = 'move'; } catch {}
                    }}
                    onDragLeave={() => {
                      if (dropTargetAccessor === column.accessor) setDropTargetAccessor(null);
                    }}
                    onDrop={(e) => {
                      if (!isDraggable) return;
                      e.preventDefault();
                      const from = dragAccessor || e.dataTransfer.getData('text/plain');
                      reorderColumn(from, column.accessor);
                      setDragAccessor(null);
                      setDropTargetAccessor(null);
                    }}
                    onDragEnd={() => { setDragAccessor(null); setDropTargetAccessor(null); }}
                    className={`
                      relative ${compact ? 'px-2 sm:px-3' : 'px-3 sm:px-5'} ${dense ? 'py-2' : 'py-3 sm:py-3.5'} text-${column.align || 'left'} text-xs font-semibold text-gray-600
                      capitalize tracking-wide select-none transition-colors
                      ${column.headerClassName || (typeof column.className === 'string' ? column.className : '') || ''}
                      ${isSortable ? 'cursor-pointer hover:bg-gray-100' : ''}
                      ${isDraggable ? 'cursor-move' : ''}
                      ${isDragging ? 'opacity-40' : ''}
                      ${isDropTarget ? 'bg-primary-100 ring-2 ring-primary-400 ring-inset' : ''}
                    `}
                    title={column.headerTitle || undefined}
                    onClick={(e) => isSortable && !isDragging && handleSort(column.accessor, e)}
                    title={isDraggable ? 'Sürükleyerek yer değiştir' : (isFirstCol ? 'Sabit kolon' : undefined)}
                  >
                    <div className={`flex items-center gap-1.5 ${wrapHeaders ? '' : 'overflow-hidden'} ${column.align === 'right' ? 'flex-row-reverse' : column.align === 'center' ? 'justify-center' : ''}`}>
                      {/* Sağa hizalı (sayı) sütunda ikonlar başlığın SOLUNA gelir — başlık rakamlarla aynı sağ kenarda biter */}
                      <span className={wrapHeaders ? 'whitespace-normal break-words leading-tight' : 'truncate'}>{column.header}</span>
                      {isSortable && (() => {
                        const si = effSorts.findIndex((s) => s.key === column.accessor);
                        if (si < 0) return <ArrowUpDown size={12} className="text-gray-300 flex-shrink-0" />;
                        const dir = effSorts[si].direction;
                        return (
                          <span className="inline-flex items-center flex-shrink-0 text-primary-500" title={`Sıralama ${si + 1}. seviye · ${dir === 'asc' ? 'artan' : 'azalan'}`}>
                            {dir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {effSorts.length > 1 && <span className="text-[9px] font-bold leading-none">{si + 1}</span>}
                          </span>
                        );
                      })()}
                      {colMenuOn(column) && (
                        <button
                          type="button"
                          data-colfunnel
                          onClick={(e) => openFilterMenu(e, column.accessor)}
                          className={`ml-0.5 flex-shrink-0 rounded p-0.5 transition-colors ${
                            isFilterActive(effColFilters[column.accessor], column)
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'}`}
                          title={colFilterable(column) ? 'Sırala · filtrele · hesapla' : 'Sırala · hesapla · taşı'}
                          aria-haspopup="menu"
                          aria-expanded={filterMenu === column.accessor}
                        >
                          {colFilterable(column) ? <Filter size={12} /> : <MoreVertical size={12} />}
                        </button>
                      )}
                    </div>
                    {/* Resize handle — sağ kenar */}
                    {resizable && tableId && (
                      <div
                        onMouseDown={(e) => onResizeStart(e, column.accessor)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary-300/40 active:bg-primary-500/60 transition-colors"
                        title="Sürükleyerek genişliği ayarla"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                <SkeletonRow key={i} columnCount={visibleColumns.length} />
              ))
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (expandedRender ? 1 : 0)}
                  className="px-3 sm:px-5 py-16 text-center text-gray-400 text-sm bg-white"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-2xl text-gray-300">⊘</span>
                    <span>{emptyMessage}</span>
                    <span className="text-xs text-gray-300">Filtre / aramayı değiştirin</span>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.flatMap((row, rowIndex) => {
                const rk = _rowKey(row, rowIndex);
                const isExpanded = expandedKeys.has(rk);
                const hasExpand = !!expandedRender;
                const totalCols = visibleColumns.length + (hasExpand ? 1 : 0) + (selectable ? 1 : 0);
                // Özel satır rengi varsa striped'ı BASMA — iki bg-* sınıfı aynı
                // özgüllükte çakışır ve kazananı stylesheet sırası belirler,
                // sınıf sırası değil (kırmızı satır beyaz görünürdü).
                const customRowCls = rowClassName ? (rowClassName(row, rowIndex) || '') : '';
                // Geri dönüşte son tıklanan satır hover rengiyle vurgulanır (tek bg sınıfı — striped/
                // custom bg ile çakışmasın diye onların YERİNE geçer, aksi halde Tailwind bg sırası belirsiz).
                const isLastClicked = !!onRowClick && !!tableId && String(rk) === String(lastRowKey);
                const rows = [
                  <tr
                    key={`row-${rk}`}
                    className={`
                      transition-colors
                      ${isLastClicked ? 'bg-primary-50' : (customRowCls || (striped ? (rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/60') : ''))}
                      ${(onRowClick || hasExpand) ? 'cursor-pointer hover:bg-primary-50' : 'hover:bg-gray-50'}
                      ${isExpanded ? 'bg-violet-50/30' : ''}
                    `}
                    onClick={() => {
                      if (hasExpand) toggleExpand(rk);
                      if (onRowClick) { markRowClicked(rk); onRowClick(row); }
                    }}
                  >
                    {selectable && (
                      // stopPropagation ŞART: satır tıklaması (detay paneli) de tetiklenirdi
                      <td className="w-10 px-3 align-middle" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox"
                               className="rounded border-gray-300 cursor-pointer disabled:opacity-40
                                          disabled:cursor-not-allowed"
                               aria-label="Satırı seç"
                               title={(!!isRowSelectable && !isRowSelectable(row) && rowSelectDisabledTitle)
                                 ? (typeof rowSelectDisabledTitle === 'function'
                                     ? rowSelectDisabledTitle(row) : rowSelectDisabledTitle)
                                 : undefined}
                               disabled={!!isRowSelectable && !isRowSelectable(row)}
                               checked={selSet.has(rk)}
                               onChange={() => toggleOne(rk)} />
                      </td>
                    )}
                    {hasExpand && (
                      <td className="w-8 px-2 align-middle">
                        {isExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-400" />}
                      </td>
                    )}
                    {visibleColumns.map((column) => {
                      const customWidth = colWidths[column.accessor] || fitWidths?.[column.accessor];
                      const effectiveWidth = customWidth || (typeof column.width === 'number' ? column.width : null);
                      const tdStyle = effectiveWidth ? { width: effectiveWidth, minWidth: effectiveWidth } : undefined;
                      return (
                        <td
                          key={`${rk}-${column.accessor}`}
                          style={tdStyle}
                          className={`${compact ? 'px-2 sm:px-3' : 'px-3 sm:px-5'} ${dense ? 'py-1' : 'py-3 sm:py-3.5'} text-sm text-gray-700 text-${column.align || 'left'} overflow-hidden ${effectiveWidth ? 'truncate' : 'whitespace-nowrap'} ${(typeof column.className === 'function' ? column.className(row) : column.className) || ''}`}
                        >
                          {column.render
                            ? column.render(row[column.accessor], row, (safePage - 1) * pageSize + rowIndex)
                            : row[column.accessor]}
                        </td>
                      );
                    })}
                  </tr>
                ];
                if (hasExpand && isExpanded) {
                  rows.push(
                    <tr key={`exp-${rk}`} className="bg-gray-50/50">
                      <td colSpan={totalCols} className="p-0 border-b border-gray-200">
                        {expandedRender(row)}
                      </td>
                    </tr>
                  );
                }
                return rows;
              })
            )}
          </tbody>
          {hasCalcRow && !loading && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                {selectable && <td className="w-10 px-3" />}
                {expandedRender && <td className="w-8 px-2" />}
                {visibleColumns.map((column) => {
                  const fn = calcs?.[column.accessor];
                  const meta = fn ? CALC_FNS.find((x) => x.key === fn) : null;
                  return (
                    <td key={`calc-${column.accessor}`}
                      className={`${compact ? 'px-2 sm:px-3' : 'px-3 sm:px-5'} py-2 text-xs text-${column.align || 'left'} overflow-hidden`}>
                      {meta && (
                        <button type="button" onClick={(e) => openFilterMenu(e, column.accessor)} data-colfunnel
                          title={`${meta.label} — filtrelenmiş tüm satırlar (${sortedData.length})`}
                          className="inline-flex flex-col leading-tight hover:text-primary-700">
                          <span className="text-[10px] text-gray-400">{meta.label}</span>
                          <span className="font-semibold text-gray-800 tabular-nums">{_fmtCalc(calcValues[column.accessor])}</span>
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination footer */}
      {pagination && totalPages >= 1 && !loading && paginatedData.length > 0 && (
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-gray-200 bg-white flex flex-wrap items-center justify-center gap-2 sm:gap-6">
          {/* < Prev */}
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
            <span className="font-medium hidden sm:inline">Prev</span>
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            {pageNumbers.map((p, i) =>
              p === '...' ? (
                <span key={`dots-${i}`} className="px-1.5 sm:px-2 text-gray-400 text-sm">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`min-w-[28px] sm:min-w-[32px] h-7 sm:h-8 px-1.5 sm:px-2 rounded text-xs sm:text-sm font-medium transition-colors ${
                    safePage === p
                      ? 'bg-gray-100 text-gray-900 border border-gray-300 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              )
            )}
          </div>

          {/* Next > */}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <span className="font-medium hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>

          {/* Spacer */}
          <div className="hidden sm:block flex-1" />

          {/* Reset widths — sadece tableId varsa ve kullanıcı genişlik ayarladıysa */}
          {resizable && tableId && hasCustomWidths && (
            <button
              onClick={onResetWidths}
              title="Kolon genişliklerini varsayılana sıfırla"
              className="text-xs text-gray-400 hover:text-gray-700"
            >
              Genişlikleri sıfırla
            </button>
          )}

          {/* Sütun seçici artık YALNIZ üst toolbar'da (mükerrer footer düğmesi kaldırıldı). */}

          {/* Per page selector */}
          <div className="relative">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="appearance-none text-xs sm:text-sm font-medium border border-gray-200 rounded-lg pl-2.5 sm:pl-3 pr-7 sm:pr-8 py-1 sm:py-1.5 bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n} / sayfa</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 sm:right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}
      {/* Sticky yatay scrollbar (viewport altında, tablo uzunsa) */}
      {stickyScroll.visible && (
        <div
          ref={fakeScrollRef}
          onScroll={onFakeScroll}
          className="fixed z-30 overflow-x-auto bg-white/90 backdrop-blur border-t border-gray-200 shadow-[0_-2px_4px_rgba(0,0,0,0.04)]"
          style={{ left: stickyScroll.left, width: stickyScroll.width, bottom: 0, height: 16 }}
          title="Tabloyu yana kaydır"
        >
          <div style={{ width: stickyScroll.contentWidth, height: 1 }} />
        </div>
      )}

      {/* Kolon huni menüsü — position:fixed (overflow'da kırpılmaz) */}
      {filterMenu && (() => {
        const col = colByAccessor.get(filterMenu);
        if (!col) return null;
        const ft = colFilterType(col);
        const f = effColFilters[filterMenu] || {};
        const isFirst = columns[0]?.accessor === filterMenu;
        // Değer listesi: getFacetOptions varsa (sunucu/kontrollü) menuFacet'ten, yoksa iç facetOptions.
        // Sunucu-facet arama server'da yapıldı → tekrar filtreleme; client/senkron ise yerel filtre.
        const _lsearch = (o) => !filterSearch || String(o.label ?? o.value ?? '').toLocaleLowerCase('tr').includes(filterSearch.toLocaleLowerCase('tr'));
        const opts = ft !== 'values' ? []
          : (getFacetOptions
              ? (menuFacet.server ? menuFacet.items : menuFacet.items.filter(_lsearch)).slice(0, 300)
              : facetOptions(col).filter(_lsearch).slice(0, 300));
        const selected = new Set(f.values || []);
        const toggleVal = (v) => {
          const cur = new Set(selected);
          if (cur.has(v)) cur.delete(v); else cur.add(v);
          setColFilter(filterMenu, cur.size ? { values: Array.from(cur) } : null);
        };
        return (
          <div
            data-colmenu-root
            className="fixed z-50 w-[280px] bg-white border border-gray-200 rounded-xl py-1 flex flex-col overflow-y-auto"
            style={{
              left: filterMenuPos.x,
              ...(filterMenuPos.up ? { bottom: filterMenuPos.bottom } : { top: filterMenuPos.y }),
              maxHeight: filterMenuPos.maxH,
              boxShadow: '0 14px 36px rgba(28,30,38,.18)',
            }}
          >
            {/* Aksiyonlar — sıralama · hesap · gizle */}
            {(() => {
              const Item = ({ icon: Ic, label, onClick, active, danger, hint }) => (
                <button type="button" onClick={onClick}
                  className={`w-full flex items-center gap-2.5 text-left px-2.5 py-1.5 text-[13px] rounded-md ${danger ? 'text-red-600 hover:bg-red-50' : active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <Ic size={14} className={danger ? '' : active ? 'text-primary-600' : 'text-gray-500'} />
                  <span className="flex-1">{label}</span>
                  {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
                </button>
              );
              const canSort = col.sortable !== false && sortable;
              const curSort = effSorts.find((x) => x.key === filterMenu);
              const isPrimary = effSorts[0]?.key === filterMenu;
              const curCalc = calcs?.[filterMenu] || null;
              const numeric = colHasNumbers(col);
              return (
                <>
                  {canSort && (
                    <div className="px-1 pb-1 border-b border-gray-100">
                      <Item icon={ArrowUp} label="Artan sırala" hint={colFilterType(col) === 'range' ? '0 → 9' : 'A → Z'} active={isPrimary && curSort?.direction === 'asc'}
                        onClick={() => { applySort(filterMenu, 'asc'); setFilterMenu(null); }} />
                      <Item icon={ArrowDown} label="Azalan sırala" hint={colFilterType(col) === 'range' ? '9 → 0' : 'Z → A'} active={isPrimary && curSort?.direction === 'desc'}
                        onClick={() => { applySort(filterMenu, 'desc'); setFilterMenu(null); }} />
                      {!_sortControlled && effSorts.length > 0 && !isPrimary && (
                        <Item icon={ListPlus} label={curSort ? 'Sonraki seviye yönünü çevir' : 'Sonra bu sütuna göre sırala'} hint="Shift+tık"
                          onClick={() => { addSort(filterMenu, curSort?.direction === 'asc' ? 'desc' : 'asc'); setFilterMenu(null); }} />
                      )}
                      {curSort && (
                        <Item icon={X} label="Sıralamayı kaldır" onClick={() => { removeSort(filterMenu); setFilterMenu(null); }} />
                      )}
                    </div>
                  )}
                  <div className="px-2.5 py-1.5 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-[13px] text-gray-700 mb-1">
                      <Calculator size={14} className="text-gray-500" /> Hesapla
                      <span className="text-[10px] text-gray-400 ml-auto">tablonun altında</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {CALC_FNS.filter((fn) => !fn.numeric || numeric).map((fn) => (
                        <button key={fn.key} type="button" onClick={() => setCalc(filterMenu, curCalc === fn.key ? null : fn.key)}
                          className={`px-1.5 py-0.5 rounded-md border text-[11px] ${curCalc === fn.key ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          {fn.label}
                        </button>
                      ))}
                      {curCalc && (
                        <button type="button" onClick={() => setCalc(filterMenu, null)} className="px-1.5 py-0.5 rounded-md text-[11px] text-gray-400 hover:text-gray-700">Kaldır</button>
                      )}
                    </div>
                  </div>
                  {col.hideable !== false && !isFirst && (
                    <div className={`px-1 py-1 ${colFilterable(col) ? 'border-b border-gray-100' : ''}`}>
                      <Item icon={EyeOff} label="Sütunu gizle" danger onClick={() => { toggleColVisible(filterMenu); setFilterMenu(null); }} />
                    </div>
                  )}
                </>
              );
            })()}

            {/* Filtre gövdesi */}
            {!colFilterable(col) ? null : ft === 'daterange' ? (
              <div className="p-2.5 flex flex-col gap-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Tarih aralığı</div>
                <DateRangePicker
                  compact
                  startDate={_isoToDate(f.min)}
                  endDate={_isoToDate(f.max)}
                  onChange={({ start, end }) => {
                    if (!start && !end) { setColFilter(filterMenu, null); return; }
                    setColFilter(filterMenu, { ...f, min: _dateToIso(start), max: _dateToIso(end) });
                  }}
                />
                <button className="text-xs text-gray-500 hover:text-gray-800 self-end" onClick={() => setColFilter(filterMenu, null)}>Temizle</button>
              </div>
            ) : ft === 'range' ? (
              <div className="p-2.5 flex flex-col gap-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Aralık</div>
                <div className="flex items-center gap-1.5">
                  <input type="number" value={f.min ?? ''} placeholder="Min"
                    onChange={(e) => setColFilter(filterMenu, { ...f, min: e.target.value })}
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1" />
                  <span className="text-gray-400">–</span>
                  <input type="number" value={f.max ?? ''} placeholder="Max"
                    onChange={(e) => setColFilter(filterMenu, { ...f, max: e.target.value })}
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1" />
                </div>
                <button className="text-xs text-gray-500 hover:text-gray-800 self-end" onClick={() => setColFilter(filterMenu, null)}>Temizle</button>
              </div>
            ) : ft === 'text' ? (
              <div className="p-2.5 flex flex-col gap-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">İçerir</div>
                <input autoFocus value={f.text ?? ''} placeholder="Metin…"
                  onChange={(e) => setColFilter(filterMenu, e.target.value ? { text: e.target.value } : null)}
                  className="w-full text-xs border border-gray-200 rounded-md px-2 py-1" />
                <button className="text-xs text-gray-500 hover:text-gray-800 self-end" onClick={() => setColFilter(filterMenu, null)}>Temizle</button>
              </div>
            ) : (
              <>
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input autoFocus value={filterSearch} placeholder="Ara…"
                      onChange={(e) => setFilterSearch(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-md pl-7 pr-2 py-1" />
                  </div>
                </div>
                <div className="overflow-y-auto flex-1 py-1">
                  {getFacetOptions && menuFacet.loading && <div className="px-3 py-2 text-xs text-gray-400 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Yükleniyor…</div>}
                  {!menuFacet.loading && opts.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Sonuç yok</div>}
                  {opts.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggleVal(o.value)} className="rounded" />
                      <span className="flex-1 truncate text-gray-800">{(o.label ?? o.value) || <span className="text-gray-400">(boş)</span>}</span>
                      <span className="text-[11px] text-gray-400 tabular-nums">{o.count}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between px-2.5 py-1.5 border-t border-gray-100">
                  <button className="text-xs text-primary-600 hover:underline" onClick={() => setColFilter(filterMenu, { values: opts.map((o) => o.value) })}>Tümünü seç</button>
                  <button className="text-xs text-gray-500 hover:text-gray-800" onClick={() => setColFilter(filterMenu, null)}>Temizle</button>
                </div>
              </>
            )}
          </div>
        );
      })()}
      {columnFiltersOn && (
        <DataTableFilterPanel
          anchor={fltBtnRef.current}
          open={fltPanelOpen}
          onClose={() => setFltPanelOpen(false)}
          root={panelRoot}
          onChange={onPanelChange}
          columns={filterCols}
          typeOf={advTypeOf}
          controlled={_fltControlled}
          getOptions={panelOptions}
          saved={savedFilters}
          onApplySaved={applySavedFilter}
          onSaveCurrent={saveCurrentFilter}
          onDeleteSaved={(id) => commitSaved(savedFilters.filter((x) => x.id !== id))}
          activeCount={panelCount + (_fltControlled ? 0 : activeFilterEntries.length)}
        />
      )}
    </div>
  );
}
